import { Divider as MuiDivider, type DividerProps as MuiDividerProps, styled } from '@mui/material';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { forwardRef, type Ref } from 'react';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

/** Legacy MUI divider with the baked-in Weave border color. Rendered only when `fpl-migration` is off. */
const LegacyMuiDivider = styled(MuiDivider)({
  borderColor: color.White08_T,
});

const styles = stylex.create({
  base: {
    border: 0,
    margin: 0,
    flexShrink: 0,
    backgroundColor: token.border,
  },
  horizontal: {
    width: '100%',
    height: 1,
  },
  /** Horizontal divider inside a flex container: stretch to the full cross-axis (width). */
  horizontalFlexItem: {
    width: '100%',
    alignSelf: 'stretch',
    height: 1,
  },
  /** Vertical dividers are always flex children in Weave; stretch to the full cross-axis (height). */
  vertical: {
    width: 1,
    alignSelf: 'stretch',
  },
});

export type AppDividerProps = MuiDividerProps;

/**
 * Weave divider and the `fpl-migration` boundary.
 *
 * - Flag off: the legacy MUI `Divider` (baked-in `White08_T` border), with every existing prop
 *   (`orientation`, `flexItem`, `textAlign`, `children`, `sx`, `className`, …) unchanged.
 * - Flag on: a native semantic separator styled with StyleX. FPL ships no divider component, so the
 *   line is owned locally: the FPL default border color, and geometry from
 *   `orientation`/`flexItem`. Matches the MUI DOM contract per variant — plain horizontal renders an
 *   `<hr>` (implicit separator); vertical renders `<div role="separator" aria-orientation="vertical">`.
 *
 * Legacy-only props (flag-on owns their concern instead): `sx` color (flag-on uses the uniform
 * color) and spacing (→ caller layout); `textAlign`/`children` (content dividers are caller-owned).
 */
const AppDivider = forwardRef<HTMLElement, AppDividerProps>(function AppDivider(props, ref) {
  const isFplMigration = useFplMigrationFlag();

  if (isFplMigration) {
    const { orientation = 'horizontal', flexItem = false, className, style, id } = props;

    if (orientation === 'vertical') {
      const vertical = stylex.props(styles.base, styles.vertical);
      return (
        <div
          ref={ref as Ref<HTMLDivElement>}
          id={id}
          role="separator"
          aria-orientation="vertical"
          className={[vertical.className, className].filter(Boolean).join(' ')}
          style={{ ...vertical.style, ...style }}
        />
      );
    }

    const horizontal = stylex.props(styles.base, flexItem ? styles.horizontalFlexItem : styles.horizontal);
    return (
      <hr
        ref={ref as Ref<HTMLHRElement>}
        id={id}
        className={[horizontal.className, className].filter(Boolean).join(' ')}
        style={{ ...horizontal.style, ...style }}
      />
    );
  }

  return <LegacyMuiDivider ref={ref as Ref<HTMLHRElement>} {...props} />;
});

export default AppDivider;
